/**
 * ============================================================================
 * PUBLIC TRANSACTION VIEW - Buyer Preview (No Login Required)
 * ============================================================================
 * 
 * Screen 1: Transaction Preview (NO LOGIN REQUIRED)
 * Buyer clicks link → Opens in browser/app
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTransaction } from '../hooks';
import { useToast, useI18n } from '../providers';
import Button from '../components/common/Button';
import Spinner from '../components/common/Spinner';

/**
 * Public Transaction View Component
 * @returns {JSX.Element}
 */
const PublicTransactionView = () => {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const { getPublicTransaction, loading } = useTransaction();
  const { showError } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [transaction, setTransaction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTransaction = async () => {
      try {
        setIsLoading(true);
        const data = await getPublicTransaction(shortCode);
        setTransaction(data);
      } catch (error) {
        showError('Transaction not found or link is invalid');
        console.error('Load transaction error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (shortCode) {
      loadTransaction();
    }
  }, [shortCode, getPublicTransaction, showError]);

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Transaction Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            This transaction link is invalid or has expired.
          </p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  // Use backend totals (amount + platform fee) so buyer pays exactly what seller set
  const amount = transaction.amount ?? 0;
  const fee = transaction.platformFee ?? Math.min(amount * 0.015, 15000);
  const totalAmount = transaction.totalAmount ?? (amount + fee);

  // Backend returns creator with limited info for public view
  const sellerName = transaction.creator?.name || transaction.creator?.fullName || 'Seller';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="text-2xl">🔒</span>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              WekaTrust Escrow
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            {sellerName} invited you to:
          </p>
        </div>

        {/* Transaction Details Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          {/* Title */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">📦</span>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {transaction.title || 'Untitled Transaction'}
              </h2>
            </div>
            {(transaction.referenceId || transaction.shortCode) && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Transaction #{transaction.referenceId || transaction.shortCode}
              </p>
            )}
          </div>

          {/* Key Details */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="text-xl">💰</span>
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Amount</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(amount, transaction.currency || 'XAF')}
                </div>
              </div>
            </div>

            {transaction.deliveryDate && (
              <div className="flex items-center gap-3">
                <span className="text-xl">⏱️</span>
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Delivery</div>
                  <div className="text-lg font-medium text-gray-900 dark:text-white">
                    {formatDate(transaction.deliveryDate)}
                  </div>
                </div>
              </div>
            )}

            {transaction.deliveryDays && (
              <div className="flex items-center gap-3">
                <span className="text-xl">⏱️</span>
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Delivery Time</div>
                  <div className="text-lg font-medium text-gray-900 dark:text-white">
                    {transaction.deliveryDays} days
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          {transaction.description && (
            <div className="mb-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description:
              </h3>
              <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                {transaction.description}
              </p>
            </div>
          )}

          {/* Protection Info */}
          <div className="mb-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              How WekaTrust protects you:
            </h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Money held safely in escrow
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Released only after delivery approval
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Dispute protection available
                </span>
              </li>
            </ul>
          </div>

          {/* Payment Breakdown */}
          <div className="bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-2xl p-6 mb-6 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Payment Breakdown:
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Amount:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(amount, transaction.currency || 'XAF')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Service fee:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatCurrency(fee, transaction.currency || 'XAF')}
                </span>
              </div>
              <div className="border-t border-blue-200 dark:border-blue-700 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {language === 'fr' ? 'Vous payez (montant exact) :' : 'You pay (exact amount):'}
                  </span>
                  <span className="font-bold text-blue-600 dark:text-blue-400 text-lg">
                    {formatCurrency(totalAmount, transaction.currency || 'XAF')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => {
                // Navigate to payment flow (use transaction id so backend resolves exact amount)
                const id = transaction.id;
                if (id) navigate(`/transactions/${id}/pay`);
                else navigate(`/transaction/${transaction.shortCode || shortCode}/pay`);
              }}
              className="text-lg py-6 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
            >
              Proceed to Pay →
            </Button>
            <Button
              variant="ghost"
              size="md"
              fullWidth
              onClick={() => navigate('/')}
              className="rounded-xl"
            >
              Decline Transaction
            </Button>
          </div>
        </div>

        {/* Security Badge */}
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            🔒 Secure payment via WekaTrust Escrow
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicTransactionView;
